import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { CheckSquare, MessageSquare, Tag, FileSpreadsheet, Phone, Calendar, Clock, Bell, ExternalLink, Reply } from "lucide-react";
import { Twemoji } from 'react-emoji-render';
import { mapMessageTemplate } from "../../lib/messageMapper";
import { formatWhatsAppToMarkdown } from "../../lib/whatsappFormatter";
import { formatPeriodicityDisplay } from "./renewalUtils";

interface StepPreviewProps {
  name: string;
  campaignType: string; // "Marketing" | "Renovación de servicio"
  templateId: string | null;
  templateContent?: string;
  templateName?: string;
  templateType?: string;
  templateLanguage?: string;
  templateNameInput?: string;
  sampleRow?: Record<string, any> | null;
  googleSheetId: string;
  googleSheetName?: string;
  googleSheetTabName?: string;
  phoneColumn: string;
  renewalDateColumn?: string; // Optional for Marketing
  dynamicDateColumn?: string; // Optional for Dynamic
  dynamicTimeColumn?: string; // Optional for Dynamic
  periodicityUnit?: "MONTHS" | "YEARS" | "DAYS" | "WEEKS"; // Optional for Marketing
  periodicityValue?: number; // Optional for Marketing
  reminderDays?: number[]; // Optional for Marketing
  sendingTime: string;
  sendDate?: string; // For Marketing campaigns
  saving: boolean;
  isCreatingNewTemplate?: boolean; // True when creating a new template (not using existing)
  columnMapping?: Record<string, string>;
  headerText?: string; // Texto del header del template
  headerType?: 'TEXT' | 'IMAGE' | 'MEDIA' | string; // Tipo de header
  headerImagePreview?: string; // URL de preview de imagen del header
  footerText?: string; // Texto del footer del template
  buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>; // Botones del template
  onBack: () => void;
  onCreate: () => void;
}

const StepPreview: React.FC<StepPreviewProps> = ({
  name,
  campaignType,
  templateId,
  templateContent,
  templateName,
  templateType,
  templateLanguage,
  templateNameInput,
  sampleRow,
  googleSheetId,
  googleSheetName,
  googleSheetTabName,
  phoneColumn,
  renewalDateColumn,
  dynamicDateColumn,
  dynamicTimeColumn,
  periodicityUnit,
  periodicityValue,
  reminderDays,
  sendingTime,
  sendDate,
  saving,
  isCreatingNewTemplate = false,
  columnMapping,
  headerText,
  headerType,
  headerImagePreview,
  footerText,
  buttons = [],
  onBack,
  onCreate,
}) => {
  const isMarketing = campaignType === "Marketing";
  const isRenewal = campaignType === "Renovación" || campaignType === "Renovación de servicio";
  const isDynamic = campaignType === "Dynamic";
  const isCalendar = campaignType === "Calendar";

  const [templateMessage, setTemplateMessage] = React.useState<string>(templateContent || "");

  React.useEffect(() => {
    setTemplateMessage(templateContent || "");
  }, [templateContent]);

  const formatPeriodicity = () => {
    if (!periodicityUnit || !periodicityValue) return "—";
    return formatPeriodicityDisplay(periodicityUnit, periodicityValue);
  };

  const remindersLabel = reminderDays && reminderDays.length > 0
    ? [...reminderDays].sort((a, b) => b - a)
      .map(d => (d === 0 ? "mismo día" : `${d} ${d === 1 ? "día" : "días"} antes`))
      .join(", ")
    : "—";

  // Label for campaign type
  const campaignTypeLabel = campaignType === "Marketing" ? "Marketing" : campaignType === "Dynamic" ? "Campaña Dinámica" : campaignType === "Calendar" ? "Campaña Calendar" : "Renovación de Servicio";

  // Format template name: remove underscores and capitalize
  const formatTemplateName = (name: string | undefined | null): string => {
    if (!name) return "—";
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  };

  // Format template type
  const formatTemplateType = (type: string | undefined | null): string => {
    if (!type) return "Standard";
    if (type.toLowerCase().includes('marketing') || type.toLowerCase().includes('promocion')) return "Marketing: Promociones y ofertas";
    if (type.toLowerCase().includes('utilidad') || type.toLowerCase().includes('utility') || type.toLowerCase().includes('alerta')) return "Utilidad: Alertas y actualizaciones";
    return type;
  };

  // Format template language
  const formatTemplateLanguage = (lang: string | undefined | null): string => {
    if (!lang) return "—";
    if (lang === 'es' || lang.toLowerCase().includes('español') || lang.toLowerCase().includes('spanish')) return "Español";
    if (lang === 'en' || lang.toLowerCase().includes('inglés') || lang.toLowerCase().includes('english')) return "Inglés";
    return lang;
  };

  const displayTemplateName = formatTemplateName(templateName || templateNameInput);
  const displayTemplateType = formatTemplateType(templateType);
  const displayTemplateLanguage = formatTemplateLanguage(templateLanguage);

  // Format time to AM/PM
  const formatTimeAMPM = (time: string) => {
    if (!time) return "09:00 AM";
    const [hours, minutes] = time.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  };

  // Format full date with time for Marketing campaigns
  const formatFullDateTime = (date: string, time: string) => {
    if (!date) return "—";
    try {
      const dateObj = new Date(`${date}T${time || "00:00"}:00`);
      const options: Intl.DateTimeFormatOptions = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
      };
      return dateObj.toLocaleDateString("es-ES", options);
    } catch {
      return `${date} ${formatTimeAMPM(time)}`;
    }
  };

  const sampleMessage = templateContent || "Tu mensaje aparecerá aquí"; // Fallback text

  // Helper function to substitute variables in text
  const substituteVariables = React.useCallback((text: string): string => {
    if (!text) return text;
    let result = text;

    // 1. Substitute numbered variables {{1}}, {{2}}... if columnMapping exists
    if (columnMapping && sampleRow) {
      const numericVars = (result.match(/\{\{\d+\}\}/g) || []).map(m => m.replace(/\{\{|\}\}/g, ''));

      numericVars.forEach(varNum => {
        const columnName = columnMapping[varNum];
        if (columnName && sampleRow[columnName] !== undefined) {
          const regex = new RegExp(`\\{\\{${varNum}\\}\\}`, 'g');
          result = result.replace(regex, String(sampleRow[columnName]));
        }
      });
    }

    // 2. Perform standard named variable substitution
    return mapMessageTemplate(result, sampleRow || {});
  }, [columnMapping, sampleRow]);

  // Generate preview header with variables replaced
  const previewHeader = React.useMemo(() => {
    return substituteVariables(headerText || '');
  }, [headerText, substituteVariables]);

  // Generate preview message with variables replaced
  // ✅ Sistema inteligente para limpiar mensaje y extraer botones de texto
  const { processedBody, extractedButtons } = React.useMemo(() => {
    const rawText = templateMessage || sampleMessage || "";
    const substituted = substituteVariables(rawText);
    
    // Pattern: matching "| [Button]" or "\n\n[Button]"
    const buttonRegex = /(?:\|\s*|(?:\r?\n)+\s*)\[([^\]]+)\]/g;
    
    // Extraer los nombres de los botones
    const foundButtons: Array<{ type: string; text: string }> = [];
    let match;
    const tempText = substituted;
    while ((match = buttonRegex.exec(tempText)) !== null) {
      if (match[1] && !foundButtons.some(b => b.text === match[1].trim())) {
        foundButtons.push({ type: 'QUICK_REPLY', text: match[1].trim() });
      }
    }
    
    // Limpiar el cuerpo del mensaje (quitar los botones del final)
    const body = tempText.replace(buttonRegex, '').trim();
    
    return { processedBody: body, extractedButtons: foundButtons };
  }, [templateMessage, sampleMessage, substituteVariables]);

  // Combinar botones de props con los extraídos del texto (evitando duplicados)
  const finalButtons = React.useMemo(() => {
    const combined = [...(buttons || [])];
    
    // Solo añadir extraídos si no hay botones estructurados
    if (combined.length === 0) {
      return extractedButtons;
    }
    
    return combined;
  }, [buttons, extractedButtons]);

  // ✅ Intelligent Tour logic for Step 5
  React.useEffect(() => {
    const isRenewal = campaignType === "Renovación" || campaignType === "Renovación de servicio";
    const tourKey = isRenewal ? 'renewal_tour_step5_seen' : 'marketing_tour_step5_seen';

    const triggerStage5 = () => {
      return setTimeout(() => {
        if (isRenewal) {
          import('@/components/common/RenewalCampaignTour').then(({ startRenewalStep5Tour }) => {
            startRenewalStep5Tour(() => localStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Renewal Step5 Tour:', e));
        } else {
          import('@/components/common/MarketingCampaignTour').then(({ startMarketingStep5Tour }) => {
            startMarketingStep5Tour(() => localStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Marketing Step5 Tour:', e));
        }
      }, 300);
    };

    let timeoutId: NodeJS.Timeout | undefined;

    const hasSeen = localStorage.getItem(tourKey);
    if (!hasSeen) {
      timeoutId = triggerStage5();
    }

    const handleRestart = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) e.detail.handled = true;
      localStorage.removeItem(tourKey);
      timeoutId = triggerStage5();
    };

    window.addEventListener('restart-local-tour', handleRestart);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('restart-local-tour', handleRestart);
    };
  }, [campaignType]);

  return (
    <div className="space-y-8 animate-fadeIn pb-10">

      {/* 1. Resumen de la campaña */}
      <Card id="tour-marketing-preview-campaign" className="rounded-2xl border-none shadow-lg bg-white overflow-hidden ring-1 ring-black/5">
        <div className="h-2 bg-gradient-to-r from-[#1CB3B3] to-[#1F4068]" />
        <CardContent className="p-4 sm:p-6 lg:p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shadow-sm ring-1 ring-teal-100">
              <CheckSquare className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Resumen de la campaña</h3>
              <p className="text-sm text-gray-500 mt-1">Revisa la configuración básica y estructura de tu campaña</p>
            </div>
          </div>

          <div className="flex flex-col gap-6 p-0 sm:p-2">

            {/* ROW 1: Campaign Name */}
            <div>
              <div className="flex items-start gap-4 p-4 bg-teal-50/50 rounded-2xl border border-teal-100/50">
                <div className="p-2.5 rounded-xl bg-teal-100 text-teal-700 shadow-sm">
                  <Tag className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-base font-bold text-gray-900 mb-0.5">Nombre de la campaña</div>
                  <div className="text-sm font-medium text-gray-600 leading-snug">{name || "Sin nombre"}</div>
                </div>
              </div>
            </div>

            {/* ROW 2: Data Source (Sheet & Columns) - Hide for Calendar campaigns */}
            {!isCalendar && (
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 pl-1">Fuente de datos</h4>
                <div className="flex flex-col xl:flex-row gap-4">
                  {/* Sheet */}
                  <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 group hover:border-blue-200 transition-colors">
                    <div className="p-2 rounded-lg bg-white text-blue-500 shadow-sm border border-gray-100 flex-shrink-0">
                      <FileSpreadsheet className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-0.5">Nombre de hoja de cálculo</div>
                      <div className="text-sm font-semibold text-gray-900 truncate" title={googleSheetName ?? googleSheetId}>
                        {googleSheetName ?? googleSheetId ?? "—"}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 group hover:border-blue-200 transition-colors">
                    <div className="p-2 rounded-lg bg-white text-blue-500 shadow-sm border border-gray-100 flex-shrink-0">
                      <FileSpreadsheet className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-0.5">
                        Pestaña seleccionada
                      </div>

                      <div
                        className="text-sm font-semibold text-gray-900 truncate"
                        title={googleSheetTabName || "—"}
                      >
                        {googleSheetTabName || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Phone Column - Hide for Dynamic (shown in Columnas Configuradas) */}
                  {!isDynamic && (
                    <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 group hover:border-blue-200 transition-colors">
                      <div className="p-2 rounded-lg bg-white text-gray-400 group-hover:text-blue-500 shadow-sm border border-gray-100 transition-colors flex-shrink-0">
                        <Phone className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-0.5">Columna de teléfono seleccionada</div>
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {phoneColumn || "Automático"}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Renewal Column - Only show for Renewal campaigns */}
                  {isRenewal && (
                    <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 group hover:border-blue-200 transition-colors">
                      <div className="p-2 rounded-lg bg-white text-gray-400 group-hover:text-blue-500 shadow-sm border border-gray-100 transition-colors flex-shrink-0">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-0.5">Columna de fecha de renovación seleccionada</div>
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {renewalDateColumn || "—"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ROW 3: Schedule - Show different fields based on campaign type */}
            {/* For Dynamic campaigns, show column mappings instead of scheduling */}
            {/* Hide entirely for Calendar campaigns */}
            {!isCalendar && (isDynamic ? (
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 pl-1">Columnas Configuradas</h4>
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Phone Column */}
                  <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-blue-200 transition-colors">
                    <div className="p-2 rounded-lg bg-white text-blue-500 shadow-sm border border-gray-100 flex-shrink-0">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-0.5">Columna de Teléfono</div>
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {phoneColumn || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Date Column */}
                  <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-teal-200 transition-colors">
                    <div className="p-2 rounded-lg bg-white text-teal-500 shadow-sm border border-gray-100 flex-shrink-0">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-0.5">Columna de Fecha</div>
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {dynamicDateColumn || "—"}
                      </div>
                    </div>
                  </div>

                  {/* Time Column */}
                  <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-indigo-200 transition-colors">
                    <div className="p-2 rounded-lg bg-white text-indigo-500 shadow-sm border border-gray-100 flex-shrink-0">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-0.5">Columna de Hora</div>
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {dynamicTimeColumn || "9:00 AM (predeterminado)"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 pl-1">Programación</h4>
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Periodicity - Only for Renewal campaigns */}
                  {isRenewal && (
                    <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-indigo-200 transition-colors">
                      <div className="p-2 rounded-lg bg-white text-indigo-500 shadow-sm border border-gray-100 flex-shrink-0">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-0.5">Periodicidad</div>
                        <div className="text-sm font-semibold text-gray-900">{formatPeriodicity()}</div>
                      </div>
                    </div>
                  )}

                  {/* Reminders - Only for Renewal campaigns */}
                  {isRenewal && (
                    <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-orange-200 transition-colors">
                      <div className="p-2 rounded-lg bg-white text-orange-500 shadow-sm border border-gray-100 flex-shrink-0">
                        <Bell className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-0.5">Recordatorios</div>
                        <div className="text-sm font-semibold text-gray-900">{remindersLabel}</div>
                      </div>
                    </div>
                  )}

                  {/* Sending Time/Date - Show for Renewal and Marketing */}
                  <div className="flex-1 basis-0 flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100 hover:border-teal-200 transition-colors">
                    <div className="p-2 rounded-lg bg-white text-teal-500 shadow-sm border border-gray-100 flex-shrink-0">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-0.5">
                        {isRenewal ? "Hora de envío" : "Fecha y hora de envío"}
                      </div>
                      <div className="text-sm font-semibold text-gray-900">
                        {isRenewal
                          ? formatTimeAMPM(sendingTime)
                          : formatFullDateTime(sendDate || "", sendingTime)
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

          </div>
        </CardContent>
      </Card>

      {/* 2. Plantilla y Mensaje */}
      <Card id="tour-marketing-preview-template" className="rounded-2xl border-none shadow-lg bg-white overflow-hidden ring-1 ring-black/5">
        <div className="h-2 bg-gradient-to-r from-[#179999] to-[#1CB3B3]" />
        <CardContent className="p-8">
          <div className="flex flex-col xl:flex-row gap-10 items-stretch">
            {/* Info Izquierda: Metadatos de la plantilla */}
            <div className="flex-1 flex flex-col gap-6">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shadow-sm ring-1 ring-teal-100">
                  <CheckSquare className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Resumen de la plantilla</h3>
                  <p className="text-sm text-gray-500 mt-1">Verifica el contenido y formato del mensaje final</p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {/* Template Name - Highlighted */}
                <div className="flex items-start gap-4 p-4 bg-teal-50/50 rounded-2xl border border-teal-100/50">
                  <div className="p-2.5 rounded-xl bg-teal-100 text-teal-700 shadow-sm">
                    <Tag className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-base font-bold text-gray-900 mb-0.5">Nombre de la plantilla</div>
                    <div className="text-sm font-medium text-gray-600 leading-snug">{displayTemplateName}</div>
                  </div>
                </div>

                {/* Type and Language */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="p-2 rounded-lg bg-white text-blue-500 shadow-sm border border-gray-100">
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 mb-0.5">Tipo de mensaje</div>
                      <div className="text-xs font-medium text-gray-600">{displayTemplateType}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="p-2 rounded-lg bg-white text-indigo-500 shadow-sm border border-gray-100">
                      <Tag className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 mb-0.5">Idioma</div>
                      <div className="text-xs font-medium text-gray-600">{displayTemplateLanguage}</div>
                    </div>
                  </div>
                </div>
              </div>

              {isCreatingNewTemplate && (
                <div className="p-5 rounded-xl bg-[#F0FDF4] border border-[#DCFCE7] shadow-[0_2px_8px_rgba(22,101,52,0.05)]">
                  <h4 className="text-[#15803d] font-bold text-sm mb-2">Nota</h4>
                  <p className="text-[#166534] text-sm leading-relaxed font-medium">
                    Esta plantilla se enviará automáticamente a Meta Business Manager para revisión y aprobación oficial. Asegúrate de que el contenido cumpla las políticas y sea el texto definitivo que se enviará.
                  </p>
                </div>
              )}
            </div>

            <div className="flex-none flex justify-center xl:justify-end xl:w-[320px]">
              {/* Phone Mockup Wrapper - Responsive: min 350px, max 550px */}
              <div className="relative w-[320px] h-[600px]">
                {/* Shadow/Glow behind phone */}
                <div className="absolute -inset-4 bg-teal-500/20 blur-xl rounded-full opacity-50"></div>

                <div className="relative mx-auto w-full h-full rounded-[2.5rem] bg-gray-900 border border-black/10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col ring-1 ring-black/5">
                  {/* Status Bar Mock */}
                  <div className="absolute top-0 w-full h-8 bg-transparent z-20 flex justify-between px-6 items-center pt-3">
                    <span className="text-[11px] text-white font-medium tracking-wide">
                      {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                    </span>
                    <div className="flex gap-1.5 items-center">
                      <div className="w-3 h-3 border-[1.5px] border-white rounded-full"></div>
                      <div className="w-4 h-2 border border-white rounded-[2px] relative flex p-[0.5px]">
                        <div className="bg-white h-full w-[90%] rounded-[0.5px]"></div>
                        <div className="absolute -right-[1.5px] top-[1.5px] h-[2px] w-[1px] bg-white rounded-r-[1px]"></div>
                      </div>
                    </div>
                  </div>

                  {/* WhatsApp Header */}
                  <div className="flex items-center justify-between px-4 py-3 pb-3 pt-10 flex-none bg-[#075E54] z-10 shadow-sm">
                    <div className="flex items-center gap-2 text-white">
                      <div className="w-5 -ml-1"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M19 12H5M12 19l-7-7 7-7" /></svg></div>
                      <img src="https://i.pravatar.cc/120?img=32" alt="avatar" className="w-9 h-9 rounded-full object-cover border border-white/20" />
                      <div className="leading-tight">
                        <div className="text-[15px] font-semibold text-white">
                          {(() => {
                            let original = "";
                            if (sampleRow && phoneColumn && sampleRow[phoneColumn]) {
                              original = String(sampleRow[phoneColumn]);
                            }
                            let clean = original.replace(/\D/g, "");
                            if (clean.startsWith("57")) clean = clean.substring(2);
                            if (clean.length > 0) clean = clean.substring(0, 11);
                            return clean ? `+57 ${clean}` : "+57 300 123 4567";
                          })()}
                        </div>
                        <div className="text-[11px] text-white/90">en línea</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#efeae2] p-3 flex-1 overflow-hidden flex flex-col relative" style={{ minHeight: 0 }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')", backgroundSize: "400px", opacity: 0.4 }} />

                    {/* ✅ Scrollable Container for Messages */}
                    <div className="flex-1 flex flex-col overflow-y-auto custom-scrollbar p-3 space-y-4" style={{ maxHeight: '420px' }}>
                      <div className="flex-1"></div> {/* Spacer to push messages down */}

                      <div className="flex flex-col items-end w-full">
                        <div className="max-w-[90%] relative z-10 group flex flex-col items-end">
                          {/* Message Bubble Container */}
                          <div className="inline-block bg-[#D9FDD3] text-base text-black px-3 py-2 rounded-lg shadow-sm break-words leading-relaxed w-full relative" style={{ borderTopLeftRadius: '8px', borderTopRightRadius: '0px', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                            {/* Header - Image or Text */}
                            {(headerImagePreview && headerType !== 'TEXT') ? (
                              <div className="mb-2 -mx-1 -mt-1">
                                <img
                                  src={headerImagePreview}
                                  alt="Header"
                                  className="w-full h-auto object-cover rounded-md"
                                />
                              </div>
                            ) : previewHeader && (
                              <div className="font-bold text-gray-900 mb-1.5 whitespace-pre-wrap text-[15px]">
                                {previewHeader}
                              </div>
                            )}

                            {/* Body */}
                            <div className="text-[14.5px] whitespace-pre-wrap [&_img]:inline [&_img]:w-5 [&_img]:h-5 [&_img]:align-text-bottom [&_img]:mx-[1px]">
                              {processedBody ? (
                                <Twemoji 
                                  text={processedBody
                                    .replace(/\{\{([^}]+)\}\}/g, (match, p1) => {
                                      // Replace variables with sample data or variable name itself
                                      const varName = p1.trim().toLowerCase();
                                      if (varName.includes('nombre')) return 'Juan Perez';
                                      if (varName.includes('fecha')) return new Date().toLocaleDateString();
                                      if (varName.includes('correo')) return 'test@reveca.io';
                                      return varName.toUpperCase();
                                    })
                                    .replace(/\*([^*]+)\*/g, '$1')
                                    .replace(/_([^_]+)_/g, '$1')
                                    .replace(/~([^~]+)~/g, '$1')
                                  } 
                                  svg 
                                />
                              ) : (
                                <p className="text-gray-400 italic">Tu mensaje aparecerá aquí...</p>
                              )}
                            </div>

                            {/* Footer */}
                            {footerText && (
                              <div className="text-[11.5px] text-gray-500 mt-1 border-t border-black/5 pt-1.5 leading-tight">
                                {footerText}
                              </div>
                            )}
                            
                            {/* Inner Timestamp (inside bubble for message-only bubbles) */}
                            {!footerText && (
                              <div className="text-[10px] text-gray-400 text-right mt-1 float-right clear-both ml-2">
                                {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                                <span className="ml-1 opacity-70">✓✓</span>
                              </div>
                            )}
                          </div>

                          {/* Buttons Rendering - Outside bubble like real WhatsApp */}
                          {finalButtons && finalButtons.length > 0 && (
                            <div className="flex flex-col gap-1.5 mt-1 w-full">
                              {finalButtons.map((btn, idx) => (
                                <div
                                  key={idx}
                                  className="bg-white text-[#00A884] text-center py-2.5 rounded-lg shadow-sm text-[14px] font-medium w-full flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors border border-gray-100"
                                >
                                  {btn.type === 'URL' && <ExternalLink className="w-3.5 h-3.5" />}
                                  {btn.type === 'PHONE_NUMBER' && <Phone className="w-3.5 h-3.5" />}
                                  {btn.type === 'QUICK_REPLY' && <Reply className="w-3.5 h-3.5" />}
                                  {btn.text}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Outer Timestamp (only if there is a footer or buttons) */}
                          {(footerText || (buttons && buttons.length > 0)) && (
                            <div className="text-[10px] text-gray-400 text-right mt-1 px-1 w-full">
                              {new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
                              <span className="ml-1 opacity-70">✓✓</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Input Bar Mock */}
                    <div className="mt-2 bg-white rounded-full px-3 py-2 flex items-center gap-3 shadow-sm flex-none z-20">
                      <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400">
                        <span className="text-xl leading-none mb-0.5">+</span>
                      </div>
                      <div className="flex-1 h-8 bg-transparent flex items-center text-sm text-gray-400">
                        Escribe un mensaje...
                      </div>
                      <div className="w-8 h-8 rounded-full bg-[#00A884] flex items-center justify-center text-white">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path></svg>
                      </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mt-12 pt-6 border-t border-gray-100 px-4 sm:px-8 pb-8">
              <Button variant="outline" onClick={onBack} className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm">
                ← Atrás
              </Button>
              <Button id="tour-marketing-preview-submit" onClick={onCreate} disabled={saving} className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-3 sm:px-8 min-h-11 h-auto py-2.5 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95 disabled:opacity-70 disabled:pointer-events-none whitespace-normal text-center leading-tight">
                {saving ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Procesando...
                  </div>
                ) : "Confirmar y Crear Campaña"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

export default StepPreview;